import { BlobServiceClient } from '@azure/storage-blob';
import * as uuid from 'uuid';

export type UploadedBlob = { id: string; name: string };

/**
 * Uploads files to the shared "uploads" container and returns one blob record
 * per file.
 *
 * Shared by the requisition, delivery, call-off and order screens, which each
 * had their own copy of this. One of those copies generated the blob id
 * outside the loop, so every file in a multi-file upload overwrote the
 * previous one.
 *
 * This throws when an upload fails instead of quietly returning a shorter
 * list, so the caller can say what went wrong. The missing-SAS case is called
 * out explicitly: the value is compiled in at build time, and where the
 * environment has not been given one the Azure client fails on an empty URL
 * with a message that means nothing to a user.
 */
export async function uploadBlobs(files: File[]): Promise<UploadedBlob[]> {
    if (!files || files.length === 0) {
        return [];
    }

    const sasUrl = process.env.REACT_APP_AZURE_BLOB_CONNECTIONSTRING || '';
    if (!sasUrl) {
        throw new Error(
            'File storage is not configured for this environment, so the upload could not be started. Please report this to support.'
        );
    }

    const containerClient = new BlobServiceClient(sasUrl).getContainerClient('uploads');
    await containerClient.createIfNotExists();

    const blobs: UploadedBlob[] = [];

    // Sequential, as before: these are often large files on site connections,
    // and uploading them in parallel competes for the same bandwidth.
    // eslint-disable-next-line no-restricted-syntax
    for (const file of files) {
        const id = uuid.v4();
        // eslint-disable-next-line no-await-in-loop
        await containerClient.getBlockBlobClient(id).uploadData(file);
        blobs.push({ id, name: file.name });
    }

    return blobs;
}

/** Turns whatever a failed upload threw into something worth showing a user. */
export function describeUploadError(error: unknown): string {
    const message = error instanceof Error ? error.message : String(error || '');
    return message
        ? `Upload failed: ${message}`
        : 'Upload failed. Please check your connection and try again.';
}
